import React, { useState, useEffect, use } from "react";
import { Comment, Task, TaskPrompt } from "@/api/types";
import { apiClient } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { CommentItem } from "./CommentItem";

import { AnimatePresence, LayoutGroup, motion } from "motion/react";

interface TaskCommentsProps {
  task: Task;
  prompt: TaskPrompt;
  allowAddComments?: boolean;
}

export default function TaskPromptComments({
  task,
  prompt,
  allowAddComments = false,
}: TaskCommentsProps) {
  const { isAuthenticated, user } = useAuth();

  const [comments, setComments] = useState<Comment[]>([]);
  const [newCommentContent, setNewCommentContent] = useState("");
  const [isPublicComment, setIsPublicComment] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch comments from the task prompt endpoint
  const fetchComments = async () => {
    try {
      setIsLoading(true);

      // Fetch the task prompt which includes all comments and nested replies
      const comments = await apiClient.get<Comment[]>(
        `/comments/?prompt_id=${prompt.id}`
      );

      if (comments) {
        setComments(comments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (prompt) {
      fetchComments();
    }
  }, [isAuthenticated, prompt]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentContent.trim() || !user) return;

    setIsSubmitting(true);
    try {
      await apiClient.post<Comment>(`/comments/`, {
        prompt: prompt.id,
        content: newCommentContent.trim(),
        author: user.id,
        isPublic: isPublicComment,
      });

      setNewCommentContent("");
      await fetchComments(); // Refresh comments
    } catch (error) {
      console.error("Failed to submit comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddReply = async () => {
    // Refresh comments to show the new reply
    await fetchComments();
  };

  const handleTagUpdate = async () => {
    await fetchComments(); // Refresh comments to show updated tags
  }

  // on visiting the page, and after loading is finished, check the fragment and scroll the page to it
  // only do this when first visiting the page, not on every re-render
  useEffect(() => {
    if (isLoading) return;

    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace("#comment-", "");
      const element = document.getElementById(`comment-${id}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });

        // find the child of the current element with class _reply-content,
        // bail if not found
        const replyContent = element.querySelector("._reply-content");
        
        if (!replyContent) return;

        // briefly highlight the element
        replyContent.classList.add("!bg-green-100", "animate-shake");

        setTimeout(() => {
          // make the highlight fade away over 300ms
          replyContent.classList.remove("!bg-green-100", "animate-shake");
          // and remove the hash from the URL without reloading the page
          // FIXME: this is to avoid re-scrolling and indicating on every re-render,
          // but it also means the user can't share a link to a specific comment after the initial load
          history.replaceState("", document.title, window.location.pathname + window.location.search);
        }, 2000);
      }
    }
  });

  return (
    <div>
      {/* New comment form */}
      {user && allowAddComments && (
        <form
          onSubmit={handleSubmitComment}
          className="mb-6 bg-white rounded-lg p-4 shadow-sm border border-gray-300"
        >
          <textarea
            value={newCommentContent}
            onChange={(e) => setNewCommentContent(e.target.value)}
            placeholder="Add a comment..."
            className="w-full p-3 border border-gray-300 rounded resize-vertical"
            rows={4}
            disabled={isSubmitting}
          />

          <div className="flex items-center justify-between mt-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isPublicComment}
                onChange={(e) => setIsPublicComment(e.target.checked)}
                className="rounded"
                disabled={isSubmitting}
              />
              <span className="text-sm text-gray-700">
                Make this comment public to all participants
              </span>
            </label>

            <button
              type="submit"
              disabled={!newCommentContent.trim() || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Posting..." : "Post Comment"}
            </button>
          </div>
        </form>
      )}

      {/* Comments list */}
      {comments.length > 0 && (
        <motion.div layout className="space-y-4">
          <AnimatePresence mode="sync">
            {comments.map((comment) => (
              <motion.div
                key={`comment-frame-${comment.id}`}
                initial={{ opacity: 1, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <CommentItem
                  key={`comment-${comment.id}`}
                  task={task}
                  comment={comment}
                  onAddReply={handleAddReply}
                  onUpdateTags={handleTagUpdate}
                  user={user}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}
    </div>
  );
}
